import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  IconButton,
  CircularProgress,
} from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ShareIcon from '@mui/icons-material/Share';
import IosShareIcon from '@mui/icons-material/IosShare';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import FolderIcon from '@mui/icons-material/Folder';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { markdownComponents } from '../components/markdownComponents';
import { getFolder, streamFolderSummary } from '../services/folders';
import { getFolderItems, deleteItem, refreshItem, updateItemFlags } from '../services/items';
import { useAuth } from '../contexts/AuthContext';
import type { Folder, Item } from '../types';
import { ItemFlags, hasFlag } from '../types';
import ItemsView, { ViewModeToggle, type ViewMode } from '../components/items/ItemsView';
import ShareDialog from '../components/sharing/ShareDialog';
import EditFolderDialog from '../components/folders/EditFolderDialog';
import DeleteFolderDialog from '../components/folders/DeleteFolderDialog';
import AddToFolderDialog from '../components/folders/AddToFolderDialog';

export default function FolderDetailPage() {
  const { folderId } = useParams<{ folderId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [folder, setFolder] = useState<Folder | null>(null);
  const [items, setItems] = useState<Item[]>([]);
  const [loading, setLoading] = useState(true);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [folderItem, setFolderItem] = useState<Item | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>(() => { const stored = localStorage.getItem('itemsViewMode'); return stored === 'card' || stored === 'list' ? stored : 'card'; });
  const handleViewModeChange = (mode: ViewMode) => { setViewMode(mode); localStorage.setItem('itemsViewMode', mode); };
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [summaryText, setSummaryText] = useState('');
  const [summaryStreaming, setSummaryStreaming] = useState(false);
  const [summaryError, setSummaryError] = useState('');
  const summaryAbortRef = useRef<AbortController | null>(null);

  const handleSummary = useCallback(async () => {
    if (!folderId) return;
    summaryAbortRef.current?.abort();
    const controller = new AbortController();
    summaryAbortRef.current = controller;
    setSummaryOpen(true);
    setSummaryText('');
    setSummaryError('');
    setSummaryStreaming(true);
    await streamFolderSummary(
      folderId,
      (chunk) => setSummaryText((prev) => prev + chunk),
      () => setSummaryStreaming(false),
      (err) => { setSummaryText(''); setSummaryError(err); setSummaryStreaming(false); },
      controller.signal,
    );
  }, [folderId]);

  const load = useCallback(async () => {
    if (!folderId) return;
    const [col, colItems] = await Promise.all([
      getFolder(folderId),
      getFolderItems(folderId),
    ]);
    setFolder(col);
    setItems(colItems);
    setLoading(false);
  }, [folderId]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!folder) {
    return <Typography>Folder not found.</Typography>;
  }

  const isOwner = folder.createdBy === user?.uid;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <IconButton onClick={() => navigate('/folders')}>
          <ArrowBackIcon />
        </IconButton>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 500 }}>
            {folder.name}
          </Typography>
          {folder.description && (
            <Typography variant="body2" color="text.secondary">
              {folder.description}
            </Typography>
          )}
        </Box>
        <ViewModeToggle value={viewMode} onChange={handleViewModeChange} />
        {items.length > 0 && (
          <IconButton onClick={handleSummary} title="Summarize folder" sx={{ overflow: 'visible' }}>
            <Box sx={{ position: 'relative', display: 'inline-flex' }}>
              <AutoAwesomeIcon />
              <FolderIcon sx={{ position: 'absolute', bottom: -4, right: -6, fontSize: 14, color: 'text.secondary', zIndex: 1, bgcolor: 'background.paper', borderRadius: '50%', p: '1px' }} />
            </Box>
          </IconButton>
        )}
        {isOwner && (
          <>
            <IconButton onClick={() => setEditDialogOpen(true)}>
              <EditIcon />
            </IconButton>
            <IconButton onClick={async () => {
              const shareUrl = `${window.location.origin}/folders/${folderId}`;
              if (navigator.share) {
                try { await navigator.share({ title: folder.name, text: folder.description || folder.name, url: shareUrl }); } catch { /* cancelled */ }
              } else {
                await navigator.clipboard.writeText(shareUrl);
              }
            }}>
              <IosShareIcon />
            </IconButton>
            <IconButton onClick={() => setShareDialogOpen(true)}>
              <ShareIcon />
            </IconButton>
            <IconButton onClick={() => setDeleteDialogOpen(true)} color="error">
              <DeleteIcon />
            </IconButton>
          </>
        )}
      </Box>

      {items.length === 0 ? (
        <Typography color="text.secondary" sx={{ textAlign: 'center', mt: 4 }}>
          No items in this folder yet.
        </Typography>
      ) : (
        <ItemsView
          items={items}
          viewMode={viewMode}
          onClick={(i) => navigate(`/items/${i.id}`)}
          onEdit={(i) => navigate(`/items/${i.id}`)}
          onRefresh={async (i) => { await refreshItem(i.id); load(); }}
          onAddToFolder={(i) => setFolderItem(i)}
          onDelete={async (i) => { await deleteItem(i.id); load(); }}
          onToggleStar={(i) => {
            const newFlags = hasFlag(i.flags, ItemFlags.STARRED)
              ? i.flags & ~ItemFlags.STARRED
              : i.flags | ItemFlags.STARRED;
            setItems((prev) => prev.map((it) => it.id === i.id ? { ...it, flags: newFlags } : it));
            updateItemFlags(i.id, newFlags);
          }}
          onTagClick={(tag) => navigate(`/search?tag=${encodeURIComponent(tag)}`)}
        />
      )}

      {folderId && (
        <ShareDialog
          open={shareDialogOpen}
          onClose={() => setShareDialogOpen(false)}
          title={folder.name}
          folderId={folderId}
          currentMembers={folder.members}
          ownerUid={folder.createdBy}
          onUpdated={load}
        />
      )}

      <EditFolderDialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        folder={folder}
        onUpdated={load}
      />

      <DeleteFolderDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        folder={folder}
        onDeleted={() => navigate('/folders')}
      />

      {folderItem && (
        <AddToFolderDialog
          open={!!folderItem}
          onClose={() => setFolderItem(null)}
          item={folderItem}
          onUpdated={load}
        />
      )}

      <Dialog open={summaryOpen} onClose={() => { summaryAbortRef.current?.abort(); setSummaryOpen(false); setSummaryStreaming(false); }} maxWidth="sm" fullWidth>
        <DialogTitle>Folder Summary</DialogTitle>
        <DialogContent>
          {summaryStreaming && !summaryText && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={24} />
            </Box>
          )}
          {summaryError && (
            <Typography color="error">{summaryError}</Typography>
          )}
          {summaryText && (
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
              {summaryText}
            </ReactMarkdown>
          )}
          {summaryStreaming && summaryText && (
            <Box
              component="span"
              sx={{
                display: 'inline-block',
                width: '0.6em',
                height: '1em',
                bgcolor: 'text.primary',
                verticalAlign: 'text-bottom',
                animation: 'blink 1s step-end infinite',
                '@keyframes blink': { '50%': { opacity: 0 } },
              }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSummaryOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
